import { Link, NavLink, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import { Microscope, Menu, X, ShieldCheck, LogOut, BookOpen } from "lucide-react";
import { Button } from "./ui/button";
import api from "../lib/api";

const NAV = [
  { to: "/", key: "nav.home", end: true, testid: "nav-home-link" },
  { to: "/test/knowledge", key: "nav.knowledge", testid: "nav-knowledge-test-link" },
  { to: "/checklist/fake-nlp", key: "nav.checklist", testid: "nav-fake-nlp-checklist-link" },
  { to: "/leaderboards", key: "nav.leaderboards", testid: "nav-leaderboard-link" },
  { to: "/blog", key: "nav.blog", testid: "nav-blog-link" },
];

function LangToggle() {
  const { lang, setLang } = useLanguage();
  return (
    <div className="flex items-center rounded-full border border-slate-200 overflow-hidden text-xs font-mono" data-testid="lang-toggle">
      {["en", "nl"].map((l) => (
        <button
          key={l}
          data-testid={`lang-${l}`}
          onClick={() => setLang(l)}
          className={`px-2.5 py-1 uppercase tracking-widest transition-colors ${lang === l ? "bg-slate-900 text-white" : "text-slate-500 hover:bg-slate-100"}`}
        >
          {l}
        </button>
      ))}
    </div>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const [pendingComments, setPendingComments] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    if (user?.role !== "admin") { setPendingComments(0); return; }
    let active = true;
    const fetchCount = () => {
      api.get("/admin/comments/pending-count")
        .then((r) => { if (active) setPendingComments(r.data.count || 0); })
        .catch(() => {});
    };
    fetchCount();
    const id = setInterval(fetchCount, 30000);
    const onEvent = (e) => { if (active) setPendingComments(e.detail || 0); };
    window.addEventListener("comments-pending", onEvent);
    return () => { active = false; clearInterval(id); window.removeEventListener("comments-pending", onEvent); };
  }, [user]);

  const linkClass = ({ isActive }) =>
    `text-sm font-medium transition-colors ${isActive ? "text-teal-700" : "text-slate-600 hover:text-slate-900"}`;

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <div className="min-h-screen flex flex-col">
      <header className="sticky top-0 z-50 backdrop-blur-md bg-white/90 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="h-16 flex items-center justify-between gap-4">
            <Link to="/" data-testid="brand-logo" className="flex items-center gap-2.5 shrink-0">
              <span className="grid place-items-center w-9 h-9 rounded-md bg-slate-900 text-white">
                <Microscope size={18} />
              </span>
              <span className="leading-none">
                <span className="block font-serif font-bold text-slate-900 text-lg tracking-tight">truthaboutnlp<span className="text-teal-600">.org</span></span>
                <span className="block font-mono text-[10px] uppercase tracking-widest text-slate-400">{t("brand.tagline")}</span>
              </span>
            </Link>

            <nav className="hidden lg:flex items-center gap-7">
              {NAV.map((n) => (
                <NavLink key={n.to} to={n.to} end={n.end} className={linkClass} data-testid={n.testid}>
                  {t(n.key)}
                </NavLink>
              ))}
              {user && (
                <NavLink to="/wiki" className={linkClass} data-testid="nav-wiki-link">
                  {t("nav.wiki")}
                </NavLink>
              )}
            </nav>

            <div className="hidden lg:flex items-center gap-3">
              <LangToggle />
              {user?.role === "admin" && (
                <Link to="/admin" data-testid="nav-admin-link" className="flex items-center gap-1.5 text-sm font-medium text-teal-700 hover:text-teal-800 relative">
                  <ShieldCheck size={16} /> {t("nav.admin")}
                  {pendingComments > 0 && (
                    <span data-testid="nav-admin-comment-badge" className="inline-grid place-items-center min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold animate-pulse">
                      {pendingComments}
                    </span>
                  )}
                </Link>
              )}
              {user ? (
                <div className="flex items-center gap-3">
                  {user.wiki_access && (
                    <span data-testid="wiki-access-badge" className="hidden xl:inline-flex items-center gap-1 rounded-full bg-teal-50 border border-teal-200 text-teal-700 text-[11px] font-mono uppercase tracking-widest px-2.5 py-1">
                      <BookOpen size={12} /> {t("wiki.accessActive")}
                    </span>
                  )}
                  <span className="text-sm text-slate-500">{user.display_name}</span>
                  <Button variant="outline" size="sm" onClick={handleLogout} data-testid="logout-button">
                    <LogOut size={15} className="mr-1.5" /> {t("nav.logout")}
                  </Button>
                </div>
              ) : (
                <Button onClick={() => navigate("/login")} data-testid="nav-login-button" className="bg-slate-900 hover:bg-slate-800 text-white rounded-full px-5">
                  {t("nav.login")}
                </Button>
              )}
            </div>

            <div className="lg:hidden flex items-center gap-3">
              <LangToggle />
              <button className="text-slate-700" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle" aria-label="Menu">
                {open ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>
        </div>

        {open && (
          <div className="lg:hidden border-t border-slate-200 bg-white px-6 py-4 space-y-3">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} end={n.end} onClick={() => setOpen(false)} className={linkClass} data-testid={`m-${n.testid}`}>
                <div className="py-1">{t(n.key)}</div>
              </NavLink>
            ))}
            {user && (
              <NavLink to="/wiki" onClick={() => setOpen(false)} className={linkClass} data-testid="m-nav-wiki-link">
                <div className="py-1">{t("nav.wiki")}</div>
              </NavLink>
            )}
            <div className="pt-3 border-t border-slate-100">
              {user?.role === "admin" && (
                <Link to="/admin" onClick={() => setOpen(false)} className="block py-1 text-teal-700 font-medium">
                  {t("nav.admin")}{pendingComments > 0 ? ` (${pendingComments})` : ""}
                </Link>
              )}
              {user ? (
                <button onClick={() => { setOpen(false); handleLogout(); }} className="block py-1 text-slate-700 font-medium">{t("nav.logout")} ({user.display_name})</button>
              ) : (
                <Link to="/login" onClick={() => setOpen(false)} className="block py-1 text-slate-900 font-semibold">{t("nav.login")}</Link>
              )}
            </div>
          </div>
        )}
      </header>

      <main className="flex-1">{children}</main>

      <footer className="border-t border-slate-200 bg-white mt-20">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-12 grid md:grid-cols-3 gap-8">
          <div>
            <div className="flex items-center gap-2 mb-3">
              <span className="grid place-items-center w-8 h-8 rounded-md bg-slate-900 text-white"><Microscope size={16} /></span>
              <span className="font-serif font-bold text-slate-900">truthaboutnlp.org</span>
            </div>
            <p className="text-sm text-slate-500 leading-relaxed max-w-xs">{t("footer.about")}</p>
          </div>
          <div>
            <h4 className="font-mono text-xs uppercase tracking-widest text-slate-400 mb-3">{t("footer.assess")}</h4>
            <ul className="space-y-2 text-sm text-slate-600">
              <li><Link to="/test/knowledge" className="hover:text-teal-700">{t("footer.knowledge")}</Link></li>
              <li><Link to="/checklist/fake-nlp" className="hover:text-teal-700">{t("footer.checklist")}</Link></li>
              <li><Link to="/leaderboards" className="hover:text-teal-700">{t("footer.leaderboards")}</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="font-mono text-xs uppercase tracking-widest text-slate-400 mb-3">{t("footer.learn")}</h4>
            <ul className="space-y-2 text-sm text-slate-600">
              <li><Link to="/blog" className="hover:text-teal-700">{t("footer.blog")}</Link></li>
              <li><Link to="/login" className="hover:text-teal-700">{t("footer.createAccount")}</Link></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-slate-100 py-5 text-center text-xs text-slate-400 font-mono space-y-1">
          <div data-testid="footer-initiative">{t("footer.initiative")}</div>
          <div>© {new Date().getFullYear()} {t("footer.copyright")}</div>
        </div>
      </footer>
    </div>
  );
}
